import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Configuration d'axios avec interceptors
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export const planningApiService = {
  // Authentification
  async login(login, password) {
    try {
      console.log('🔐 Tentative de connexion:', { login, url: `${API_BASE_URL}/auth/login` });
      const response = await api.post('/auth/login', { login, password });
      console.log('✅ Connexion réussie:', response.data);
      
      const { token, user } = response.data;
      
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      return { success: true, user };
    } catch (error) {
      console.error('❌ Erreur de connexion:', error);
      console.error('   Response data:', error.response?.data);
      console.error('   Status:', error.response?.status);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Erreur de connexion' 
      };
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  },

  // Gestion des administrateurs
  async getAdmins() {
    try {
      const response = await api.get('/admins');
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des administrateurs:', error);
      throw error;
    }
  },

  async createAdmin(adminData) {
    try {
      const response = await api.post('/admins', adminData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création de l'administrateur:", error);
      throw error;
    }
  },

  async updateAdmin(adminId, adminData) {
    try {
      const response = await api.put(`/admins/${adminId}`, adminData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'administrateur:", error);
      throw error;
    }
  },

  async deleteAdmin(adminId) {
    try {
      await api.delete(`/admins/${adminId}`);
      return { success: true };
    } catch (error) {
      console.error("Erreur lors de la suppression de l'administrateur:", error);
      throw error;
    }
  },

  async changeUserRole(userId, role) {
    try {
      const response = await api.put(`/users/${userId}/role`, { role });
      return response.data;
    } catch (error) {
      console.error('Erreur lors du changement de rôle:', error);
      throw error;
    }
  },

  // Gestion des utilisateurs (tous)
  async getAllUsers() {
    try {
      const response = await api.get('/users');
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement de tous les utilisateurs:', error);
      throw error;
    }
  },

  async setUserActive(userId, isActive) {
    try {
      const response = await api.put(`/users/${userId}/active`, { isActive });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'état actif de l'utilisateur:", error);
      throw error;
    }
  },

  // Gestion des ouvriers
  async getWorkers() {
    try {
      const response = await api.get('/workers');
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des ouvriers:', error);
      throw error;
    }
  },

  async createWorker(workerData) {
    try {
      const response = await api.post('/workers', workerData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'ouvrier:', error);
      throw error;
    }
  },

  async updateWorker(workerId, workerData) {
    try {
      const response = await api.put(`/workers/${workerId}`, workerData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'ouvrier:', error);
      throw error;
    }
  },

  async deleteWorker(workerId) {
    try {
      await api.delete(`/workers/${workerId}`);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'ouvrier:', error);
      throw error;
    }
  },

  // Gestion des projets
  async getProjects() {
    try {
      const response = await api.get('/projects');
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des projets:', error);
      throw error;
    }
  },

  async createProject(projectData) {
    try {
      const response = await api.post('/projects', projectData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du projet:', error);
      throw error;
    }
  },

  // Gestion du planning
  async getPlanning(startDate, endDate) {
    try {
      const response = await api.get('/planning', {
        params: {
          startDate: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`,
          endDate: `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement du planning:', error);
      throw error;
    }
  },

  async addPlanningEntry(entryData) {
    try {
      const response = await api.post('/planning', entryData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'entrée:', error);
      throw error;
    }
  },

  async updatePlanningEntry(entryId, entryData) {
    try {
      const response = await api.put(`/planning/${entryId}`, entryData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'entrée:', error);
      throw error;
    }
  },

  async deletePlanningEntry(entryId) {
    try {
      await api.delete(`/planning/${entryId}`);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'entrée:', error);
      throw error;
    }
  },

  async bulkUpdatePlanning(assignments) {
    try {
      const response = await api.post('/planning/bulk', { assignments });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour en lot:', error);
      throw error;
    }
  }
  //
};